<template>
    <div v-show="stepActive" class="tournament-awards">
        <h2 class="title">{{ $t('title_TournamentAwards') }}</h2>
        <p class="subtitle">{{ $t('subtitle_TournamentAwards') }}</p>

        <div class="info-card">
            <v-form class="awards-form">
                <draggable 
                    v-model="localAwards" 
                    @start="dragStart"
                    @end="dragEnd"
                    @change="handleChange"
                    handle=".drag-handle"
                    :animation="200"
                    ghost-class="ghost-award"
                    drag-class="dragging-award"
                    :move="checkMove"
                    :key="forceRefresh"
                    tag="div"
                    :component-data="{
                        tag: 'div',
                        name: !isDragging ? 'award-animation' : null
                    }">
                    <div v-for="(award, index) in sortedAwards" 
                        :key="award.id" 
                        class="award-container mb-4"
                        :class="{ 'is-dragging': isDragging && dragIndex === index }">
                        <div class="d-flex align-center award-row">
                            <div class="award-left d-flex align-center">
                                <div class="drag-handle">
                                    <i class="fas fa-regular fa-grip-vertical" style="color: #9A9A9A;"></i>
                                </div>
                                <span class="position-number">{{ getPositionWithSuffix(index + 1) }}</span>
                            </div>
                            <div class="award-inputs-container">
                                <div class="award-inputs d-flex">
                                    <v-text-field v-model="award.name" :label="$t('awardName')"
                                        :error-messages="getErrorMessage(award, 'name')" outlined dense class="mr-2"
                                        :counter="maxAwardTextLength"
                                        @input="handleInput(index, 'name')" @blur="handleBlur(index, 'name')"></v-text-field>
                                    <v-text-field v-model="award.description" :label="$t('description_TournamentAwards')"
                                        :error-messages="getErrorMessage(award, 'description')" outlined dense class="mr-2"
                                        @input="handleInput(index, 'description')" :counter="maxAwardTextLength"
                                        @blur="handleBlur(index, 'description')"></v-text-field>
                                    <v-text-field v-model="award.value" :label="$t('estimatedValue')" type="number"
                                        :error-messages="getErrorMessage(award, 'value')" outlined dense prefix="$"
                                        @input="handleInput(index, 'value')" @blur="handleBlur(index, 'value')">
                                    </v-text-field>
                                </div>
                            </div>
                            <div class="award-actions d-flex align-center">
                                <v-btn v-if="index > 0" icon small class="action-btn delete-btn ml-2"
                                    @click="removeAward(index)">
                                    <i class="fa-regular fa-trash" style="color: white !important;"></i>
                                </v-btn>
                                <v-btn v-if="index === awards.length - 1" icon small class="action-btn add-btn ml-2"
                                    @click="addAward">
                                    <i class="fa-regular fa-plus" style="color: black;"></i>
                                </v-btn>
                            </div>
                        </div>
                    </div>
                </draggable>
            </v-form>
        </div>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import draggable from 'vuedraggable'

export default {
    name: 'TournamentAwards',
    components: {
        draggable
    },
    props: {
        stepActive: {
            type: Boolean,
            required: true
        },
        setNextBtnDisabled: {
            type: Function,
            required: true
        }
    },
    data: () => ({
        isDragging: false,
        dragIndex: null,
        forceRefresh: 0, // Add this to help with forced refreshes if needed
        maxAwardTextLength: 35
    }),
    computed: {
        ...mapState({
            awards: state => state.tournamentForm.awards
        }),
        localAwards: {
            get() {
                return this.awards;
            },
            set(value) {
                this.updateTournamentAwards(value);
            }
        },
        sortedAwards() {
            // Always ensure we have a stable sort
            return [...this.localAwards].sort((a, b) => {
                if (!a.position) a.position = 1;
                if (!b.position) b.position = 1;
                return a.position - b.position;
            });
        }
    },
    methods: {
        ...mapMutations(['updateTournamentAwards']),
        generateUniqueId() {
            return `award-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        },
        getPositionWithSuffix(position) {
            if (this.$i18n.locale === 'es' || this.$i18n.locale === 'en') {
                return `${position}º`;
            }
            
            const suffixes = ['th', 'st', 'nd', 'rd'];
            const v = position % 100;
            return position + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
        },
        updatePositions(awards) {
            // Create a new array with updated positions
            return awards.map((award, index) => {
                // Ensure each award has a unique ID
                if (!award.id) {
                    award.id = this.generateUniqueId();
                }
                return {
                    ...award,
                    position: index + 1
                };
            });
        },
        addAward() {
            const currentAwards = [...this.awards];
            const newAward = {
                id: this.generateUniqueId(),
                position: (currentAwards.length + 1), // Add position property
                name: '',
                description: '',
                value: '',
                isDirty: {
                    name: false,
                    description: false,
                    value: false
                },
                errors: {}
            };
            
            // Add new award and update all positions
            const updatedAwards = this.updatePositions([...currentAwards, newAward]);
            
            this.updateTournamentAwards(updatedAwards);
            this.validateAllAwards();
        },
        removeAward(index) {
            const currentAwards = [...this.localAwards];
            currentAwards.splice(index, 1);
            
            // Update positions after removal
            const updatedAwards = this.updatePositions(currentAwards);
            
            this.updateTournamentAwards(updatedAwards);
            this.$nextTick(() => {
                this.validateAllAwards();
            });
        },
        handleInput(index, field) {
            const award = this.awards[index];
            award.isDirty[field] = true;
            this.validateAward(award);
            this.validateAllAwards();
        },
        handleBlur(index, field) {
            this.handleInput(index, field);
        },
        getErrorMessage(award, field) {
            return award.isDirty[field] ? award.errors[field] || '' : '';
        },
        validateAward(award) {
            const errors = {};

            const name = (award.name || '').trim();
            const desc = (award.description || '').trim();

            if (!name) {
                errors.name = this.$t('errors.required');
            } else if (name.length > this.maxAwardTextLength) {
                errors.name = this.$t('AwardNameError', { max: this.maxAwardTextLength });
            }

              if (!desc) {
                errors.description = this.$t('errors.required');
            } else if (desc.length > this.maxAwardTextLength) {
                errors.description = this.$t('AwardDescriptionError', { max: this.maxAwardTextLength });
            }

            const value = parseFloat(award.value);
            if (!award.value || award.value.trim() === '') {
                errors.value = this.$t('errors.required');
            } else if (isNaN(value) || value < 0) {
                errors.value = this.$t('errors.greaterThanZero');
            }

            award.errors = errors;
            return Object.keys(errors).length === 0;
        },
        validateAllAwards() {
            if (this.awards.length === 0) {
                this.setNextBtnDisabled(true);
                return false;
            }

            // Validate all awards
            const areAllValid = this.awards.every(award => {
                return !award.isDirty || this.validateAward(award);
            });

            this.setNextBtnDisabled(!areAllValid);
            return areAllValid;
        },
        onDragEnd() {
            // Validate all awards after reordering
            this.validateAllAwards();
        },
        checkMove(evt) {
            // Cancel move if parent node is missing
            if (!evt.draggedContext.element || !evt.relatedContext.element) {
                return false;
            }
            return true;
        },
        dragStart(evt) {
            this.isDragging = true;
            this.dragIndex = evt.oldIndex;
            document.body.classList.add('dragging-active');
            // Disable transitions during drag
            this.$el.classList.add('dragging');
        },
        dragEnd(evt) {
            this.isDragging = false;
            this.dragIndex = null;
            document.body.classList.remove('dragging-active');
            this.$el.classList.remove('dragging');
            
            if (evt.oldIndex !== evt.newIndex) {
                // Get current awards
                const currentAwards = [...this.localAwards];
                
                // Update positions
                const updatedAwards = this.updatePositions(currentAwards);
                
                // Update store
                this.$nextTick(() => {
                    this.updateTournamentAwards(updatedAwards);
                    this.validateAllAwards();
                    this.forceRefresh++;
                });
            }
        },
        handleChange(evt) {
            // Get current awards list
            let currentAwards = [...this.localAwards];
            
            // Handle moved item
            if (evt.moved) {
                const { newIndex, oldIndex } = evt.moved;
                // Ensure we're not creating duplicate references
                const movedItem = { ...currentAwards[oldIndex] };
                currentAwards = currentAwards.filter((_, index) => index !== oldIndex);
                currentAwards.splice(newIndex, 0, movedItem);
            }
            
            // Update all positions sequentially
            const updatedAwards = this.updatePositions(currentAwards);
            
            // Update store with properly ordered awards
            this.$nextTick(() => {
                this.updateTournamentAwards(updatedAwards);
                this.forceRefresh++;
            });
        }
    },
    created() {
        if (this.awards.length === 0) {
            this.addAward(); // Adds first award with validation
        } else {
            // Ensure initial positions are correct
            const updatedAwards = this.updatePositions(
                this.awards.map(award => ({
                    ...award,
                    id: award.id || this.generateUniqueId()
                }))
            );
            if (JSON.stringify(updatedAwards) !== JSON.stringify(this.awards)) {
                this.updateTournamentAwards(updatedAwards);
            }
            this.validateAllAwards();
        }
    }
}
</script>



<style lang="scss" scoped>
.tournament-awards {
    width: 100%;

    .title {
        font-size: 32px !important;
        font-weight: bold;
        margin-bottom: 8px;
        color: var(--v-textPrimary-base);
        font-family: "Avenir", sans-serif !important;
    }

    .subtitle {
        font-size: 16px;
        color: var(--v-textSecondary-base);
        margin-bottom: 24px;
        font-family: "Avenir", sans-serif;
    }

    .info-card {
        background-color: var(--v-cardBackground-base);
        border: 1px solid #9A9A9A;
        border-radius: 10px;
        padding: 76px 60px 50px 60px;

        .awards-form {
            // max-width: 720px;
            margin: auto;

            .award-container {
                .position-number {
                    font-weight: bold;
                    font-size: 18px;
                    min-width: 30px;
                    margin-bottom: 26px;
                }

                .award-inputs {
                    flex-grow: 1;
                    gap: 0;
                }

                .action-btn {
                    width: 36px !important;
                    height: 36px !important;
                    border-radius: 8px !important;
                    margin-bottom: 26px;
                    padding: 10px 28px;

                    &.delete-btn {
                        background-color: #9A9A9A !important;
                        margin-left: auto;
                    }

                    &.add-btn {
                        background-color: var(--v-mainGreen-base) !important;
                    }
                }

                .drag-handle {
                    cursor: grab;
                    padding: 8px;
                    color: #9A9A9A;  // Updated color
                    opacity: 0.4;    // Changed from 0.8 to 0.5
                    margin-right: 8px;
                    margin-bottom: 24px;
                    
                    &:active {
                        cursor: grabbing;
                    }

                    i {
                        font-size: 16px;
                    }
                }
            }
        }
    }
}

// Add animation styles
.award-animation-enter-active {
    animation: slide-fade-in 0.3s ease-out;
}

.award-animation-leave-active {
    animation: slide-fade-out 0.3s ease-in;
}

@keyframes slide-fade-in {
    from {
        transform: translateY(-20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

@keyframes slide-fade-out {
    from {
        transform: translateY(0);
        opacity: 1;
    }
    to {
        transform: translateY(-20px);
        opacity: 0;
    }
}

::v-deep .v-input__icon--append .v-icon {
    font-size: 14px;
    color: #9A9A9A;
}

.award-container {
    transition: all 0.3s ease;
    background: var(--v-cardBackground-base);
    border-radius: 8px;
    position: relative; // Add this
    z-index: 1; // Add this
    transform: translate3d(0, 0, 0); // Force GPU acceleration
    will-change: transform; // Optimize animations
    
    &.is-dragging {
        opacity: 0.5;
        z-index: 2; // Higher z-index while dragging
    }

    .drag-handle {
        cursor: grab;
        padding: 8px;
        
        &:active {
            cursor: grabbing;
        }
    }
}

.ghost-award {
    opacity: 0.5;
    background: var(--v-cardBackground-base);
    border: 2px dashed #9A9A9A;
    height: auto !important;
    padding: 16px 0;
}

.dragging-award {
    opacity: 1 !important;
}

// Improve transition animations
.award-animation-move {
    transition: transform 0.3s ease;
}

.award-animation-enter-active {
    transition: all 0.3s ease-out;
}

.award-animation-leave-active {
    transition: all 0.3s ease-in;
    position: absolute;
}

.award-animation-enter-from,
.award-animation-leave-to {
    opacity: 0;
    transform: translateY(30px);
}

// Add styles for drag state
.dragging-active {
    cursor: grabbing;
    
    * {
        cursor: grabbing !important;
    }
}

.dragging {
    .award-animation-move,
    .award-animation-enter-active,
    .award-animation-leave-active {
        transition: none !important;
    }
}

.award-row {
  width: 100%;
  gap: 0;
}

.award-left {
  min-width: 80px; // Fixed width for left section
}

.award-inputs-container {
  flex: 1;
  width: 100%;
  max-width: 800px; // Set maximum width for inputs container
  margin: 0 16px;

  .award-inputs {
    display: grid;
    grid-template-columns: 1fr 1fr 120px; // Fixed proportions for inputs
    gap: 0;
    width: 100%;

    .v-text-field {
      width: 100%;
    }
  }
}

.award-actions {
  min-width: 90px; // Fixed width for actions section
  justify-content: flex-end;
}

.award-container {
  // ...existing styles...

  .position-number {
    // ...existing styles...
    margin-bottom: 0; // Remove bottom margin
    text-align: center;
  }

  .action-btn {
    // ...existing styles...
    margin-bottom: 0; // Remove bottom margin
  }

  .drag-handle {
    // ...existing styles...
    margin-bottom: 0; // Remove bottom margin
  }
}

.award-row {
  width: 100%;
  display: grid;
  grid-template-columns: 80px minmax(0, 1fr) 90px; // Fixed columns for left, middle, and right sections
  align-items: center;
}

.award-left {
  display: flex;
  align-items: center;
}

.award-inputs-container {
  width: 100%;
  min-width: 486px; // Allows container to shrink if needed

  .award-inputs {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 120px; // Fixed last column, flexible others
    gap: 0;
    width: 100%;
  }
}

.award-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  min-width: 90px; // Space for two buttons
}
</style>
